import { ReportCategoryName } from "../constants/system-constants";
import { HoursWorkedReport } from "../models/HoursWorkedReport";
import { getDayName } from "./DateUtils";
import { AuditReport } from "../models/AuditReport";
import { getFormattedDate } from "../services/utilities/dateFormatting";

export const getItemPath = (reportCategoryName: string, all = false) => {
    let pathSegment: string;
    switch (reportCategoryName) {
        case ReportCategoryName.TRANSACTIONAL:
            pathSegment = all ? 'transaction-reports' : 'transaction-report';
            break;
        case ReportCategoryName.TIME_AND_ATTENDANCE:
            pathSegment = all ? 'time-attendance-reports' : 'time-attendance-report';
            break;
        case ReportCategoryName.ACCESS_CONTROL:
            pathSegment = all ? 'access-control-reports' : 'access-control-report';
            break;
        case ReportCategoryName.SYSTEM_SETUP:
            pathSegment = all ? 'system-setup-reports' : 'system-setup-report';
            break;
        case ReportCategoryName.EMERGENCY_AND_SAFETY:
            pathSegment = all ? 'emergency-safety-reports' : 'emergency-safety-report';
            break;
        case ReportCategoryName.COMPLIANCE:
            pathSegment = all ? 'compliance-reports' : 'compliance-report';
            break;
        case ReportCategoryName.MY_REPORTS:
            pathSegment = all ? 'my-report' : 'my-reports';
            break;
        default:
            pathSegment = 'report' 
            break;
    }
    return `/access-control/reports/${pathSegment}/`;
}

export const getReportAttributes = (auditReport: any) => auditReport?.attributes ?? auditReport ?? {};

// Convert ISO 8601 format (e.g. "2024-06-22T14:30:00.000") to display format (e.g. "2024/06/22 14:30:00"):
// Convert ISO 8601 datetime string to display format (e.g. "2026/06/24 09:49" or "2026/06/24 14:30:00"):
// - strip timezone suffix (Z, +HH:MM, -HH:MM)
// - strip any milliseconds suffix (e.g. ".000")
// - replace the "T" separator between date and time with a space
// - replace hyphens with slashes for the date portion
export const formatISODateTime = (value?: string): string => {
    if (!value) {
        return '';
    }
    return value
        .replace(/([+-]\d{2}(:\d{2})?|Z)$/, '')
        .replace(/\.\d+$/, '')
        .replace('T', ' ')
        .replace(/-/g, '/');
};

export const formatDateOnly = (value?: string): string => {
    if (!value) {
        return '';
    }
    return getFormattedDate(value.substring(0, 10));
};

export const buildAuditSessionParentRow = (auditReport: any, detailCount: number): AuditReport => {
    const attributes = getReportAttributes(auditReport);
    return {
        id: auditReport["id"],
        entityId: '',
        sessionId: `${attributes.auditSessionId ?? ''}`,
        name: typeof attributes.asUserLoginName === 'string' ? attributes.asUserLoginName : '',
        action: attributes.aatDisplayName ?? '',
        userLoginId: attributes.userLoginId,
        type: '',
        subType: '',
        oldValue: '',
        newValue: '',
        ipAddress: attributes.asIp ?? '',
        dateTimeUtc: formatISODateTime(attributes.asDatetimeLocal),
        dateTimeLocal: formatISODateTime(attributes.asDatetimeLocal),
        dateOnly: formatDateOnly(attributes.asDatetimeLocal),
        detailCount,
        rowType: 'parent'
    };
};

export const buildAuditEntityGroupRow = (auditReport: any, detailCount: number): AuditReport => {
    const attributes = getReportAttributes(auditReport);
    const entityId = `${attributes.adEntityId ?? ''}`;
    const auditType = attributes.atDisplayName ?? attributes.atName ?? '';
    const entityLabel = entityId ? `${auditType} [${entityId}]` : auditType || 'Grouped changes';
    return {
        id: -Number(auditReport["id"]),
        entityId,
        sessionId: '',
        name: '',
        action: '',
        type: entityLabel,
        subType: '',
        oldValue: '',
        newValue: '',
        ipAddress: '',
        dateTimeUtc: '',
        dateTimeLocal: '',
        dateOnly: '',
        detailCount,
        rowType: 'child-group'
    };
};

export const getAuditEntityGroupKey = (auditReport: any): string => {
    const attributes = getReportAttributes(auditReport);
    const auditType = attributes.atDisplayName ?? attributes.atName ?? 'Grouped changes';
    const entityId = attributes.adEntityId ?? '';
    return `${auditType}::${entityId}`;
};

export const buildAuditSessionChildRow = (auditReport: any, includeEntityId = true): AuditReport => {
    const attributes = getReportAttributes(auditReport);
    return {
        id: auditReport["id"],
        entityId: includeEntityId ? `${attributes.adEntityId ?? ''}` : '',
        sessionId: '',
        name: '',
        action: '',
        type: '',
        subType: attributes.astName ?? '',
        oldValue: attributes.adValueOld ?? '',
        newValue: attributes.adValueNew ?? '',
        ipAddress: '',
        dateTimeUtc: '',
        dateTimeLocal: '',
        dateOnly: '',
        rowType: 'child'
    };
};

export const getAuditSessionDetails = (sessionReports: any[]): any[] => {
    const nestedDetails = sessionReports.flatMap((sessionReport) => {
        const attributes = getReportAttributes(sessionReport);
        return Array.isArray(attributes.details) ? attributes.details : [];
    });
    return nestedDetails.length > 0 ? nestedDetails : sessionReports;
};

export const buildAuditSessionChildRows = (sessionDetails: any[]): AuditReport[] => {
    const childRows: AuditReport[] = [];
    const reportsByEntityId = new Map<string, any[]>();
    sessionDetails.forEach((auditReport) => {
        const entityKey = getAuditEntityGroupKey(auditReport);
        const existingReports = reportsByEntityId.get(entityKey);
        if (existingReports) {
            existingReports.push(auditReport);
            return;
        }
        reportsByEntityId.set(entityKey, [auditReport]);
    });
    reportsByEntityId.forEach((entityReports) => {
        const firstAuditReport = entityReports[0];
        childRows.push(buildAuditEntityGroupRow(firstAuditReport, entityReports.length));
        childRows.push(...entityReports.map((auditReport) => buildAuditSessionChildRow(auditReport, false)));
    });
    return childRows;
};

export const buildHoursWorkedParentRow = (item: any, detailCount: number): HoursWorkedReport => {
    const attr = item.attributes ?? item;
    return {
        id: item.id,
        displayName: attr.mstDisplayname ?? '',
        firstName: attr.mstFirstname ?? '',
        middleName: attr.mstMiddlename ?? '',
        lastName: attr.mstLastname ?? '',
        title: attr.mstTitle ?? '',
        idNumber: attr.mstIdnumber ?? '',
        gender: attr.mstGender ?? '',
        current: attr.mstCurrent === 1,
        departmentId: attr.departmentId ?? null,
        department: attr.deptName ?? '',
        companyId: attr.companyId ?? null,
        company: attr.compName ?? '',
        siteId: attr.site?.id ?? attr.siteId ?? null,
        site: attr.site?.siteName ?? '',
        profileId: attr.profileId ?? null,
        profileName: attr.profileName ?? '',
        shiftType: attr.shiftType ?? '',
        totalHoursWorked: attr.totalHoursWorked ?? '',
        totalTimeSpan: attr.totalTimeSpan ?? '',
        daysWorked: attr.daysWorked,
        avgFirstInTime: attr.avgFirstInTime ?? '',
        avgLastOutTime: attr.avgLastOutTime ?? '',
        avgHoursPerDay: attr.avgHoursPerDay ?? '',
        detailCount,
        rowType: 'parent',
    };
};

export const buildHoursWorkedChildRow = (detail: any, parentId: string, index: number): HoursWorkedReport => {
    return {
        id: -(parseInt(parentId) * 1000 + index + 1),
        dateOnly: detail.workDate ?? '',
        dayOfWeek: getDayName(detail.workDate),
        firstIn: detail.firstIn ?? '',
        lastOut: detail.lastOut ?? '',
        timeSpan: detail.timeSpan ?? '',
        hoursWorked: detail.hoursWorked ?? '',
        segments: detail.segments ?? '',
        notes: detail.notes ?? '',
        rowType: 'child',
    };
};